export type FoilVariant = "purple" | "blue" | "gold" | "crimson";

export type FoilGradient = {
  from: string;
  via: string;
  to: string;
  ink: string;
  accent: string;
};

export const FOIL_VARIANTS: Record<FoilVariant, FoilGradient> = {
  purple: { from: "#dd54eb", via: "#8a5cf0", to: "#3d1a63", ink: "#2a0f42", accent: "#ffd6fb" },
  blue: { from: "#5cc8ff", via: "#5460ed", to: "#1e2270", ink: "#101445", accent: "#c9ecff" },
  gold: { from: "#ffd166", via: "#f59e0b", to: "#7c4a03", ink: "#3a2205", accent: "#fff2c9" },
  crimson: { from: "#ff6b8a", via: "#ef4444", to: "#6b1220", ink: "#3d0a12", accent: "#ffd7de" },
};

/** Pick a stable foil colourway from any id/name so a pack always looks the same. */
export function foilFor(key: string): FoilVariant {
  const order: FoilVariant[] = ["purple", "blue", "gold", "crimson"];
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return order[hash % order.length]!;
}

function parseHex(hex: string): [number, number, number] | null {
  const match = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!match) return null;
  const n = Number.parseInt(match[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("")}`;
}

function mixRgb(a: [number, number, number], b: [number, number, number], t: number): string {
  return rgbToHex(a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t);
}

/** Build a foil gradient from a single brand hex (used when BO sets foilColor). */
export function gradientFromHex(hex: string): FoilGradient | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const white: [number, number, number] = [255, 255, 255];
  const black: [number, number, number] = [0, 0, 0];
  return {
    from: mixRgb(rgb, white, 0.35),
    via: rgbToHex(rgb[0], rgb[1], rgb[2]),
    to: mixRgb(rgb, black, 0.55),
    ink: mixRgb(rgb, black, 0.78),
    accent: mixRgb(rgb, white, 0.72),
  };
}

export type PackFoilSource = {
  id: string;
  foilLabel?: string | null;
  foilColor?: string | null;
};

export type PackVisualSource = PackFoilSource & {
  images?: string[] | null;
};

export function resolvePackFoil(pack: PackFoilSource): { label: string; gradient: FoilGradient } {
  const variant = foilFor(pack.id);
  const fallback = FOIL_VARIANTS[variant];
  const custom = pack.foilColor ? gradientFromHex(pack.foilColor) : null;
  const label = pack.foilLabel?.trim() || variant.toUpperCase();
  return { label, gradient: custom ?? fallback };
}

export function resolvePackVisual(pack: PackVisualSource): {
  mode: "artwork" | "foil";
  image: string | null;
  label: string;
  gradient: FoilGradient;
} {
  const foil = resolvePackFoil(pack);
  const image = pack.images?.[0]?.trim() || null;
  if (image) {
    return { mode: "artwork", image, label: foil.label, gradient: foil.gradient };
  }
  return { mode: "foil", image: null, label: foil.label, gradient: foil.gradient };
}
